/**
 * Block 14 «Раскрой» — PR #21 EP-5 (CUT-5.1). Клиентский запуск движка раскроя
 * (ADR-0079): web импортирует `@pomoshnik/cutting-engine` и вызывает `optimize()`
 * в браузере (главный поток, офлайн-безопасно). Серверный fallback — CUT-4.6 (позже).
 *
 * Чистые функции (без сети/DOM/состояния): `buildEngineInput` собирает вход движка
 * из редактируемого `CuttingDocument`, `runCutting` прогоняет `optimize`. Управление
 * 5 состояниями (idle/loading/success/empty/error) — в сторе (`useCuttingStore`).
 *
 * @see ADR-0079, ADR-0074 (optimize), ADR-0078 (OptimizationResult — зеркало Layout)
 */

import { optimize, type EngineInput, type OffcutSpec } from '@pomoshnik/cutting-engine';
import type { OptimizationResult, SheetSizeConfig } from '@pomoshnik/shared';
import { isCompleteOffcut } from '../model/offcutsUtils';
import { documentToProjectData } from '../state/projectSync';
import { DEFAULT_UNIFORM_SHEET } from '../model/defaults';
import type { CuttingDocument, WarehouseOffcut } from '../model/types';

/**
 * Гарантировать движку валидный `sheetConfig` (PR #23, фикс падения раскроя).
 * Движок `resolveSheetSpec` бросает на `uniform` без `uniformSheet` и `mixed` без
 * `mixedVariants` — а дефолтный документ создаётся с `{mode:'uniform'}` без размера
 * (контракт-дефолт намеренно пустой, см. `defaults.ts`). Если пользователь не задал
 * размер листа на экране «Настройки», подставляем канонический UI-дефолт 2750×1830
 * (`DEFAULT_UNIFORM_SHEET`) — раскрой работает из коробки, контракт/движок не трогаем.
 * Заданный пользователем размер не перетирается (back-compat).
 */
function resolveSheetConfig(cfg: SheetSizeConfig): SheetSizeConfig {
  if (cfg.mode === 'uniform' && !cfg.uniformSheet) {
    return { mode: 'uniform', uniformSheet: { ...DEFAULT_UNIFORM_SHEET } };
  }
  if (cfg.mode === 'mixed' && !cfg.mixedVariants) {
    return { mode: 'uniform', uniformSheet: { ...DEFAULT_UNIFORM_SHEET } };
  }
  return cfg;
}

/**
 * `CuttingDocument` (+ склад) → `EngineInput`. Детали берутся из
 * `documentToProjectData` (полные + contract-форма: 0×0-«леса» отфильтрованы,
 * пазы/обработка срезаны). `settings` — `doc.settings` (включает `sheetConfig`).
 *
 * PR #24 (ADR-0081): остатки в раскрой берутся из ВЫБРАННЫХ складских записей —
 * резолв `doc.participatingOffcutIds` → склад (`warehouse`) → `OffcutSpec`.
 * Порядок выбора сохраняется; отсутствующий (удалённый из склада) id молча
 * пропускается (dangling выбор не крэшит раскрой); неполные (0×0) отфильтрованы.
 * `useOffcutsFirst` авто-включается при наличии хотя бы одного годного выбранного
 * остатка (движок читает offcuts лишь при `useOffcutsFirst === true`); иначе
 * сохраняется явная пользовательская настройка.
 *
 * PR #25: «Метод раскроя» (`settings.method`) подключён к prefer-флагам нажима
 * остатков — `'length'`→long-only, `'width'`→wide-only, `'optimal'`→оба false
 * (макс КИМ). Перетирает контракт-дефолты prefer-флагов (оба `true`). Движок и
 * контракт не трогаем — чистый web-вывод флагов из уже существующего UI-поля.
 */
export function buildEngineInput(
  doc: CuttingDocument,
  warehouse: readonly WarehouseOffcut[] = [],
): EngineInput {
  const data = documentToProjectData(doc);

  const byId = new Map(warehouse.map((o) => [o.id, o]));
  const offcuts: OffcutSpec[] = doc.participatingOffcutIds
    .map((id) => byId.get(id)) // резолв id склада → запись
    .filter((o): o is WarehouseOffcut => o !== undefined && isCompleteOffcut(o)) // dangling/0×0 → пропуск
    .map((o) => ({
      offcutId: o.id,
      length: o.length,
      width: o.width,
      qty: o.qty,
      material: o.material,
    }));

  // PR #25: «Метод раскроя» (settings.method) → нажим остатков. До этого метод
  // писался в doc.settings, но движок его игнорировал (читает только prefer-флаги),
  // а контракт-дефолты prefer-флагов оба true → мягкий нажим «длинные+широкие» был
  // включён всегда. Подключаем мягко, без размена КИМ и без усиления бонуса:
  //   'length'  → длинные остатки (long-only), 'width' → широкие (wide-only),
  //   'optimal' → нажим выключен (оба false) = максимум КИМ.
  const preferLongOffcuts = data.settings.method === 'length';
  const preferWideOffcuts = data.settings.method === 'width';

  // PR #23: гарантировать движку валидный размер листа (иначе resolveSheetSpec бросает).
  // PR #24: авто-вкл useOffcutsFirst при выбранных годных остатках (иначе движок их игнорит).
  // prefer-флаги — ПОСЛЕ спреда: метод перетирает контракт-дефолты (оба true).
  const settings = {
    ...data.settings,
    sheetConfig: resolveSheetConfig(data.settings.sheetConfig),
    preferLongOffcuts,
    preferWideOffcuts,
    ...(offcuts.length > 0 ? { useOffcutsFirst: true } : {}),
  };
  return { parts: data.parts, settings, offcuts };
}

/**
 * Прогон движка раскроя по документу + складу. Возвращает `OptimizationResult`
 * (зеркало engine `Layout`, ADR-0078). Детерминирован (тот же вход → тот же результат).
 */
export function runCutting(
  doc: CuttingDocument,
  warehouse: readonly WarehouseOffcut[] = [],
): OptimizationResult {
  return optimize(buildEngineInput(doc, warehouse));
}
